import { memo, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { CheckCircle } from '../../../../components/Icons'
import { useMediaQueryContext } from '../../../../contexts/MediaQueryContext'
import { useSetImageStyle } from '../../../../hooks/apiHooks/twinHooks'
import {
  useTwinData,
  useTwinErrors,
  useTwinPreview,
} from '../../../../hooks/storeHooks/useTwin'
import { useStyle } from '../../hooks/useStyle'
import { Heading } from '../Heading'

interface StyleUiProps {
  previewType: 'camera' | 'upload'
  openNextTab: () => void
}

const StyleUi: React.FC<StyleUiProps> = memo(({ previewType, openNextTab }) => {
  const navigate = useNavigate()
  const { isMd } = useMediaQueryContext()
  const { errors } = useTwinErrors()

  const { previewUrl, styledOriginUrls, setPreviewUrl, setStyledOriginUrls } =
    useTwinPreview()

  const { audioUrl } = useTwinData()

  const [displayedImageUrl, setDisplayedImageUrl] = useState(previewUrl)

  const { mutateAsync: setImageStyle, isPending } = useSetImageStyle()

  const {
    genders,
    categories,
    filteredImageStyles,
    selectedGender,
    selectedCategory,
    selectedImageStyle,
    handleGenderChange,
    handleCategoryChange,
    handleImageStyleChange,
  } = useStyle()

  const handleRevert = () => {
    handleImageStyleChange('')
  }

  const handleSetStyle = async (imageStyle: string) => {
    handleImageStyleChange(imageStyle)
    if (styledOriginUrls[imageStyle]) return
    const { data } = await setImageStyle({
      image_url: previewUrl || '',
      avatar_style_id: imageStyle,
    })

    setStyledOriginUrls({
      [imageStyle]: data?.url || previewUrl || '',
    })
  }

  const candidateImageUrl = useMemo(() => {
    return selectedImageStyle
      ? styledOriginUrls[selectedImageStyle]
      : styledOriginUrls.original
  }, [selectedImageStyle, styledOriginUrls])

  useEffect(() => {
    if (candidateImageUrl) {
      setDisplayedImageUrl(candidateImageUrl)
    }
  }, [candidateImageUrl])

  const handleContinue = () => {
    setPreviewUrl(displayedImageUrl || '')
    if (errors.consent === 'notMatched' && audioUrl) {
      navigate('/generate-twin/consent')
    } else {
      openNextTab()
    }
  }

  return (
    <>
      <Heading
        title="Turn Your Selfie Into a Studio Shot"
        subtitle="Choose any look you like"
      />
      <div className="flex justify-center items-stretch rounded-2xl w-full md:w-[750px] aspect-video tc-mv-blur">
        <div className="py-6 px-4 flex-1">
          <div className="flex justify-between items-center gap-2 flex-row">
            <div className="flex rounded-[100px] gap-1 bg-black p-1 flex-1">
              {genders.map(gender => (
                <button
                  key={gender}
                  data-testid={`style-gender-${gender.toLowerCase()}-btn`}
                  className={`px-4 py-2 rounded-[100px] text-sm flex-1 ${
                    selectedGender === gender
                      ? 'bg-[rgba(128,128,128,0.3)]'
                      : ''
                  }`}
                  onClick={() => handleGenderChange(gender)}
                >
                  {gender}
                </button>
              ))}
            </div>
            <div className="flex rounded-[100px] gap-1 bg-black p-1 flex-1">
              {categories.map(category => (
                <button
                  key={category}
                  data-testid={`style-category-${category.toLowerCase()}-btn`}
                  className={`px-3 py-2 rounded-[100px] text-sm flex-1 ${
                    selectedCategory === category
                      ? 'bg-[rgba(128,128,128,0.3)]'
                      : ''
                  }`}
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          {isMd && (
            <div className="flex gap-1 mt-4 justify-between flex-wrap sm:flex-nowrap">
              {filteredImageStyles.map(imageStyle => (
                <button
                  key={imageStyle.id}
                  data-testid={`style-image-${imageStyle.id}-btn`}
                  className="rounded-2xl overflow-hidden aspect-video relative block w-[49%] sm:w-1/2  min-w-[150px]"
                  onClick={() => handleSetStyle(imageStyle.id)}
                >
                  {selectedImageStyle === imageStyle.id ? (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      {isPending ? (
                        <AnimatedSpinner />
                      ) : (
                        <CheckCircle size={24} className="text-white" />
                      )}
                    </div>
                  ) : null}
                  <img src={imageStyle.sample_url} alt={imageStyle.title} />
                </button>
              ))}
            </div>
          )}
          <img
            src={displayedImageUrl || ''}
            alt="Preview mt-4"
            className={`w-full object-cover rounded-2xl aspect-video mt-5 block ${
              previewType === 'camera' ? '-scale-x-100' : ''
            }`}
          />
        </div>
        {!isMd && (
          <div className="pr-4 py-6 w-44 rounded-tr-2xl rounded-br-2xl max-h-[60vh] overflow-auto min-w-fit flex-col gap-1">
            {filteredImageStyles.map(imageStyle => (
              <button
                key={imageStyle.id}
                data-testid={`style-image-mobile-${imageStyle.id}-btn`}
                className="rounded-2xl overflow-hidden aspect-video relative w-40 block"
                onClick={() => handleSetStyle(imageStyle.id)}
              >
                {selectedImageStyle === imageStyle.id ? (
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    {isPending ? (
                      <AnimatedSpinner />
                    ) : (
                      <CheckCircle size={24} className="text-white" />
                    )}
                  </div>
                ) : null}
                <img src={imageStyle.sample_url} alt={imageStyle.title} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-4 flex-wrap mt-8 w-full">
        <button
          data-testid="style-revert-btn"
          className="btn btn-secondary sm:!grow-0 !grow"
          onClick={handleRevert}
          disabled={isPending || !selectedImageStyle}
        >
          Revert
        </button>
        <button
          data-testid="style-continue-btn"
          className="btn btn-primary sm:!grow-0 !grow"
          disabled={isPending}
          onClick={handleContinue}
        >
          Continue
          <i className="fa-solid fa-arrow-right text-lg ml-2" />
        </button>
      </div>
    </>
  )
})

export { StyleUi }
